// utils/dataLoaders.js
import { db } from '../services/firebase';
import { collection, getDocs, query, where, orderBy, Timestamp, addDoc, getDoc, deleteDoc, updateDoc, doc } from 'firebase/firestore';
import { startOfMonth, endOfMonth, addDays } from 'date-fns';

const devLog = (...args) => process.env.NODE_ENV === 'development' && console.log(...args);

/**
 * Normalise un Timestamp Firestore (ou Date) en midi UTC du bon jour calendaire parisien.
 * Les dates sont stockées à minuit heure Paris (UTC+1/+2) = veille en UTC.
 * Cette fonction interprète la date dans le fuseau Europe/Paris et renvoie midi UTC
 * du jour correct — identique pour tous les utilisateurs quelle que soit leur timezone.
 */
// Formateur créé UNE SEULE FOIS (Intl.DateTimeFormat est coûteux à instancier)
const _parisFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Paris',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});

export const normalizeToParisDate = (tsOrDate) => {
  if (!tsOrDate) return new Date(NaN);
  const d = tsOrDate?.toDate ? tsOrDate.toDate() : new Date(tsOrDate);
  const parisDateStr = _parisFormatter.format(d); // → "YYYY-MM-DD" du bon jour en heure Paris
  return new Date(parisDateStr + 'T12:00:00Z'); // midi UTC = jour identique en tout fuseau ±12h
};

/**
 * Charge toutes les données initiales nécessaires
 */
export const loadInitialData = async (adminMode = false) => {
  const data = {
    roomTypes: [],
    rooms: [],
    availabilityRules: [],
    blockedDates: [],
    defaultRules: {
      minNights: 1,
      maxNights: 30,
      customMessage: '',
      weekConfig: {
        monday: 'flexible',
        tuesday: 'flexible',
        wednesday: 'flexible',
        thursday: 'flexible',
        friday: 'flexible',
        saturday: 'flexible',
        sunday: 'flexible'
      }
    },
    reservations: []
  };

  try {
    // Charger les types de chambres
    const typesSnapshot = await getDocs(
      query(collection(db, 'roomTypes'), orderBy('sortOrder', 'asc'))
    );
    data.roomTypes = typesSnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      // ✅ En mode admin, inclure les types internes
      .filter(rt => adminMode || !rt.isInternal);
    
    devLog(`✓ Types de chambres chargés (adminMode: ${adminMode}):`, data.roomTypes.length);
    devLog('Types disponibles:', data.roomTypes.map(rt => `${rt.name} (${rt.id})${rt.isInternal ? ' [INTERNE]' : ''}`).join(', '));
    
    // Charger TOUTES les chambres (pas seulement available)
    const roomsSnapshot = await getDocs(collection(db, 'rooms'));
    const allRooms = roomsSnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter(r => r.status === 'available' || r.status === 'active' || !r.status);
    
    // IMPORTANT: Ajouter roomTypeId à chaque chambre en se basant sur roomIds dans RoomType
    // ✅ Utiliser TOUS les types (y compris internes si adminMode)
    const allTypes = typesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    data.rooms = allRooms.map(room => {
      // Trouver le type de chambre qui contient cet ID de chambre (dans TOUS les types)
      const roomType = allTypes.find(rt => 
        rt.assignedRooms && rt.assignedRooms.includes(room.id)
      );
      
      return {
        ...room,
        roomTypeId: roomType ? roomType.id : null,
        isInternal: roomType ? roomType.isInternal : false
      };
    })
    // ✅ Filtrer selon adminMode
    .filter(room => {
      if (room.roomTypeId === null) return false;
      // En mode admin, inclure toutes les chambres
      // En mode public, exclure les chambres internes
      return adminMode || !room.isInternal;
    });
    
    devLog(`✓ Chambres chargées et liées aux types (adminMode: ${adminMode}):`, data.rooms.length);
    
    // DEBUG: Afficher la structure exacte des chambres
    devLog('Structure des chambres:');
    data.rooms.forEach(room => {
      devLog(`  Chambre ${room.roomNumber}:`, {
        id: room.id,
        roomTypeId: room.roomTypeId,
        isInternal: room.isInternal
      });
    });
    
    // Détail par type de chambre
    const roomsByType = {};
    data.rooms.forEach(room => {
      if (!roomsByType[room.roomTypeId]) {
        roomsByType[room.roomTypeId] = [];
      }
      roomsByType[room.roomTypeId].push(room.roomNumber || room.id);
    });
    
    devLog('Répartition des chambres par type:');
    Object.entries(roomsByType).forEach(([typeId, roomNumbers]) => {
      const type = data.roomTypes.find(rt => rt.id === typeId) || allTypes.find(rt => rt.id === typeId);
      devLog(`  - ${type?.name || typeId}${type?.isInternal ? ' [INTERNE]' : ''}: ${roomNumbers.length} chambres (${roomNumbers.join(', ')})`);
    });
    
    // Charger les règles de disponibilité
    const rulesSnapshot = await getDocs(
      query(collection(db, 'availability_rules'), orderBy('priority', 'desc'))
    );
    data.availabilityRules = rulesSnapshot.docs.map(doc => {
      const docData = doc.data();
      const rule = {
        id: doc.id,
        ...docData,
        startDate: normalizeToParisDate(docData.startDate),
        endDate: normalizeToParisDate(docData.endDate),
        priority: docData.priority || 1,
        minNights: docData.minNights || 1,
        maxNights: docData.maxNights || 30,
        roomTypes: docData.roomTypes || [],
        customMessage: docData.customMessage || '',
        appliedTo: docData.appliedTo || 'all', // NOUVEAU : pour le système membres
        weekConfig: docData.weekConfig || {}
      };
      
      devLog(`✓ Règle "${rule.name}":`, {
        période: `${rule.startDate.toLocaleDateString()} - ${rule.endDate.toLocaleDateString()}`,
        nuits: `${rule.minNights}-${rule.maxNights}`,
        priorité: rule.priority,
        types: rule.roomTypes.length > 0 ? rule.roomTypes : 'Tous'
      });
      
      return rule;
    });
    
    devLog('✓ Total règles de disponibilité:', data.availabilityRules.length);
    
    // Charger les dates bloquées
    const blockedSnapshot = await getDocs(collection(db, 'blocked_dates'));
    data.blockedDates = blockedSnapshot.docs.map(doc => {
      const docData = doc.data();
      return {
        id: doc.id,
        date: normalizeToParisDate(docData.date),
        reason: docData.reason || '',
        roomTypes: docData.roomTypes || []
      };
    });
    
    devLog('✓ Dates bloquées chargées:', data.blockedDates.length);
    
    // Charger les règles par défaut
    try {
      const defaultRulesDoc = await getDocs(
        query(collection(db, 'settings'), where('type', '==', 'default_availability'))
      );
      
      if (!defaultRulesDoc.empty) {
        const defaultData = defaultRulesDoc.docs[0].data();
        
        devLog('✓ Règles par défaut trouvées:', {
          minNights: defaultData.minNights,
          maxNights: defaultData.maxNights,
          weekConfig: defaultData.weekConfig
        });
        
        if (defaultData.weekConfig) {
          data.defaultRules.weekConfig = defaultData.weekConfig;
        }
        
        data.defaultRules.minNights = defaultData.minNights || 1;
        data.defaultRules.maxNights = defaultData.maxNights || 30;
        data.defaultRules.maxRoomsPerBooking = defaultData.maxRoomsPerBooking || 5;
        data.defaultRules.customMessage = defaultData.customMessage || '';
        
      } else {
        console.warn('⚠️ Aucune règle par défaut trouvée dans Firebase!');
        devLog('Utilisation des règles par défaut du système');
      }
    } catch (error) {
      console.error('❌ Erreur chargement règles par défaut:', error);
    }

    devLog('\n=== RÉCAPITULATIF CHARGEMENT ===');
    devLog('✓ roomTypes:', data.roomTypes.length);
    devLog('✓ rooms:', data.rooms.length);
    devLog('✓ availabilityRules:', data.availabilityRules.length);
    devLog('✓ blockedDates:', data.blockedDates.length);
    devLog('✓ defaultRules configurées');
    devLog('=================================\n');

  } catch (error) {
    console.error('❌ Erreur lors du chargement initial:', error);
    throw error;
  }

  return data;
};

/**
 * Charge les réservations pour un mois donné
 */
export const loadReservationsForMonth = async (currentMonth) => {
  try {
    // Définir la plage de dates avec une marge
    const startDate = startOfMonth(currentMonth);
    const endDate = addDays(endOfMonth(currentMonth), 7);
    
    devLog(`\n📅 Chargement des réservations`);
    devLog(`   Période: ${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`);
    
    // 🔒 SÉCURITÉ : au lieu de lire la collection Firestore `reservations` (qui contient les
    // noms clients), on interroge le backend qui renvoie l'occupation SANS donnée personnelle
    // (status, dates, roomId uniquement). Permet de fermer `reservations` au public.
    // Endpoint : GET /api/availability/occupancy (voir "FUTUR ENDPOINT DISPO" à la racine du repo).
    const apiUrl = process.env.REACT_APP_API_URL;
    const from = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
    const resp = await fetch(`${apiUrl}/availability/occupancy?from=${from}`);
    if (!resp.ok) throw new Error(`availability endpoint HTTP ${resp.status}`);
    const { occupancy } = await resp.json();

    devLog(`   Occupation chargée depuis le backend (sans PII): ${occupancy.length}`);

    // Filtrer côté client : checkIn <= fin de période + exclure annulées
    // Les checkIn/checkOut sont pré-normalisés ici pour ne pas recalculer à chaque rendu du calendrier
    const reservations = occupancy
      .map(data => ({
        ...data,
        _checkInNorm: normalizeToParisDate(data.checkIn),
        _checkOutNorm: normalizeToParisDate(data.checkOut)
      }))
      .filter(res => {
        // Exclure les annulées
        if (res.status === 'cancelled') return false;

        // Vérifier le chevauchement avec le mois
        return res._checkInNorm <= endDate;
      });
    
    devLog('   Réservations pour la période:', reservations.length);
    
    // Détail par statut
    const statuses = {};
    reservations.forEach(res => {
      statuses[res.status] = (statuses[res.status] || 0) + 1;
    });
    devLog('   Par statut:', statuses);
    
    return reservations;
    
  } catch (error) {
    console.error('❌ Erreur lors du chargement des réservations:', error);
    return [];
  }
};

/**
 * Créer un blocage temporaire
 */
export const createTemporaryBooking = async (roomId, checkIn, checkOut, sessionId) => {
  try {
    const docRef = await addDoc(collection(db, 'temporary_bookings'), {
      roomId,
      checkIn: Timestamp.fromDate(checkIn),
      checkOut: Timestamp.fromDate(checkOut),
      sessionId,
      // 🔵 SUPPRIMÉ : expiresAt (on utilise createdAt + 10min)
      createdAt: Timestamp.now()
    });
    
    devLog('✔ Temporary booking créé:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ Erreur création temporary booking:', error);
    throw error;
  }
};

/**
 * Renseigner le nom du client sur des blocages temporaires (dès qu'il est connu, avant paiement)
 */
export const updateTemporaryBookingsGuestName = async (temporaryBookingIds, guestName) => {
  try {
    await Promise.all(
      (temporaryBookingIds || []).map(id => updateDoc(doc(db, 'temporary_bookings', id), { guestName }))
    );
  } catch (error) {
    console.error('❌ Erreur mise à jour nom sur temporary bookings:', error);
  }
};

/**
 * Supprimer un blocage temporaire
 */
export const deleteTemporaryBooking = async (temporaryBookingId) => {
  try {
    await deleteDoc(doc(db, 'temporary_bookings', temporaryBookingId));
    devLog('✔ Temporary booking supprimé:', temporaryBookingId);
  } catch (error) {
    console.error('❌ Erreur suppression temporary booking:', error);
  }
};

/**
 * Charger et nettoyer les temporary bookings
 */
export const loadAndCleanTemporaryBookings = async () => {
  try {
    const snapshot = await getDocs(collection(db, 'temporary_bookings'));
    const now = new Date();
    const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000); // 🔵 10 MINUTES
    const active = [];
    
    devLog(`🔍 Vérification de ${snapshot.docs.length} temporary bookings...`);
    
    for (const doc of snapshot.docs) {
      const data = doc.data();
      const createdAt = data.createdAt.toDate();
      
      // 🔵 Vérifier avec le temps de création au lieu de expiresAt
      if (createdAt < tenMinutesAgo) {
        // Supprimer les expirés (plus de 10 minutes)
        await deleteDoc(doc.ref);
        devLog(`🗑️ Nettoyage temporary booking expiré (>10min): ${doc.id}`);
      } else {
        active.push({
          id: doc.id,
          ...data,
          checkIn: data.checkIn.toDate(),
          checkOut: data.checkOut.toDate(),
          createdAt // 🔵 Garder createdAt au lieu de expiresAt
        });
      }
    }
    
    devLog(`✔ ${active.length} temporary bookings actifs`);
    return active;
  } catch (error) {
    console.error('❌ Erreur chargement temporary bookings:', error);
    return [];
  }
};

/**
 * Créer une nouvelle réservation
 */
// ── LA SOCIÉTÉ QUI VEND LE SÉJOUR ───────────────────────────────────────────
//
// Une chambre appartient à une société : c'est elle qui encaisse, sur SON
// compte Stripe, et c'est son nom qui figurera sur la note.
//
// La règle de l'exploitant : « le canal suit la chambre ». On prend la société
// de la PREMIÈRE chambre attribuée, et on l'ESTAMPE définitivement sur la
// réservation.
//
// 🔴 DÉFINITIVEMENT, c'est le mot important. Si l'on déplace ensuite le client
// de la 1.14 vers la 3.01 — deux chambres, deux sociétés — la réservation garde
// celle de la 1.14. Sinon l'acompte et le solde partiraient sur deux entités
// différentes, et la carte enregistrée sur le Stripe de la première serait
// débitée au nom de la seconde.
//
// Avant cette estampe (22/08/2026), rien ne portait de société : 185 notes de
// caisse sur 359 n'avaient aucun SIREN, donc étaient indéclarables en
// e-reporting.
const societeDeLaPremiereChambre = async (donnees) => {
  const roomId = donnees.roomId
    || (Array.isArray(donnees.rooms) ? donnees.rooms.map((r) => r && r.roomId).find(Boolean) : null);
  if (!roomId) return null;
  try {
    const snap = await getDoc(doc(db, 'rooms', roomId));
    return snap.exists() ? ((snap.data() || {}).companyId || null) : null;
  } catch (e) {
    // Une lecture ratée ne doit pas empêcher de réserver : mieux vaut une
    // réservation sans société — rattrapable — qu'un client qui ne peut pas
    // réserver.
    console.error('Société de la chambre :', e);
    return null;
  }
};

export const createReservation = async (reservationData) => {
  try {
    devLog('\n📝 Création d\'une nouvelle réservation');
    devLog('   Client:', `${reservationData.client.firstName} ${reservationData.client.name}`);
    devLog('   Chambre:', reservationData.roomId);
    devLog('   Dates:', `${reservationData.checkIn.toDate().toLocaleDateString()} - ${reservationData.checkOut.toDate().toLocaleDateString()}`);
    devLog('   Prix total:', `${reservationData.pricing.total}€`);
    
    // La société est posée ICI, une fois pour toutes. Une valeur déjà fournie
    // par l'appelant l'emporte : elle vient d'un choix explicite.
    const companyId = reservationData.companyId || await societeDeLaPremiereChambre(reservationData);
    console.log('   Société :', companyId || 'AUCUNE (chambre non affectée)');

    const docRef = await addDoc(collection(db, 'reservations'), { ...reservationData, companyId: companyId || null });
    
    devLog('✔ Réservation créée avec succès:', docRef.id);
    
    return docRef.id;
  } catch (error) {
    console.error('❌ Erreur lors de la création de la réservation:', error);
    throw error;
  }
};